/**
 * Render a parked mission's `awaiting_user` questions as READ-ONLY lines.
 *
 * Why this is not `questions.map(String)`.
 *
 * The wire types this field `z.array(z.unknown())`
 * (`mission-service/src/contract.ts`, `MissionAwaitingUserSchema`) — the
 * HARNESS declares each entry's shape, not the coordinator and not this
 * SPA. So the vocabulary is OPEN: today's entries look like
 * `{question_id, text}`, tomorrow's may carry a different text field or
 * be bare strings. A closed reader over an open vocabulary renders an
 * empty list against perfectly good data, which reads to a lawyer as
 * "the agents are waiting on nothing" — a false claim built from a
 * negative, the defect shape this pane keeps producing.
 *
 * Two invariants therefore hold, and both are asserted in the tests:
 *
 *   1. COUNT IN === COUNT OUT. An entry we cannot read is never dropped.
 *      Dropping would silently shorten the list the human-gate depends
 *      on, and a shorter list looks identical to a shorter question set.
 *   2. AN UNREADABLE ENTRY SAYS SO. It is marked `unreadable` and carries
 *      its raw serialization, so the surface can render a visibly
 *      abnormal row (the `CaseChip` precedent in `case-label.ts`:
 *      announcing failure is not the same as substituting for it). It
 *      must NEVER collapse to `""`, because a blank line renders as a
 *      question with no text — the reassuring direction.
 *
 * ⚠ EMPTY STRING IS HOW THIS WIRE SPELLS "ABSENT", and `??` does not
 * catch it. Every field pick below is a first-NON-BLANK pick, not a
 * first-non-nullish one.
 *
 * ⚠ Question TEXT is never truncated — the lawyer is answering it, and a
 * clipped question can change its meaning. Only the diagnostic blob on an
 * unreadable entry is capped, because that is debug output, not content.
 *
 * Pure + IO-free. Answering stays chat-mediated: nothing here produces an
 * affordance, by ruling (handoff `lexide-team-agents-missions-surface-
 * handoff-2026-06-22.md` §5 — a pane-side resume bypasses the concierge's
 * approval gating for irreversible/court-facing acts).
 */

/** One rendered line. `unreadable` rows are shown, marked, never hidden. */
export interface AwaitingQuestionLine {
  /** Stable key for React. Falls back to the index — never to the text,
   *  which may repeat across entries. */
  key: string;
  /** The harness's own id when it declared one (`question_id` / `id`). */
  id?: string;
  /** Human text, or the capped raw form when `unreadable`. Never blank. */
  text: string;
  /** True when no human-readable text could be found in the entry. */
  unreadable: boolean;
}

/** Text-bearing field names seen or plausibly declared by a harness, in
 *  precedence order. Additive by design: an unknown shape degrades to
 *  `unreadable`, it does not throw and does not vanish. */
const TEXT_FIELDS = ["text", "question", "prompt", "label", "title"] as const;
const ID_FIELDS = ["question_id", "questionId", "id"] as const;

/** Diagnostic blobs only — see the docblock: content is never clipped. */
const RAW_CAP = 300;

/** A value is usable text only if it is a string with non-space content.
 *  `""` and `"   "` are ABSENT on this wire, not empty answers. */
function nonBlank(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function pick(
  entry: Record<string, unknown>,
  fields: readonly string[],
): string | undefined {
  for (const field of fields) {
    const hit = nonBlank(entry[field]);
    if (hit) return hit;
  }
  return undefined;
}

/** Last-resort rendering of an entry we could not read, capped because it
 *  is diagnostic. Never returns a blank string. */
function rawForm(entry: unknown): string {
  let raw: string;
  try {
    raw = typeof entry === "string" ? entry : JSON.stringify(entry);
  } catch {
    // Circular / non-serialisable — still must not vanish.
    raw = String(entry);
  }
  if (!nonBlank(raw)) raw = String(entry);
  raw = raw.trim();
  if (!raw) raw = "?";
  return raw.length > RAW_CAP ? `${raw.slice(0, RAW_CAP)}…` : raw;
}

/**
 * Map the wire's questions to renderable lines.
 *
 * `undefined`/absent yields `[]` — an ABSENT list, which the caller must
 * render as today's block unchanged rather than as an empty question set.
 */
export function formatAwaitingQuestions(
  questions: readonly unknown[] | undefined,
): AwaitingQuestionLine[] {
  if (!questions?.length) return [];
  return questions.map((entry, index) => {
    const key = `q-${index}`;
    if (entry !== null && typeof entry === "object" && !Array.isArray(entry)) {
      const record = entry as Record<string, unknown>;
      const text = pick(record, TEXT_FIELDS);
      const id = pick(record, ID_FIELDS);
      if (text) return { key, id, text, unreadable: false };
      // An object with an id but no readable text is still a real
      // question the harness is blocked on — surface it, marked.
      return { key, id, text: rawForm(entry), unreadable: true };
    }
    const plain = nonBlank(entry);
    if (plain) return { key, text: plain, unreadable: false };
    return { key, text: rawForm(entry), unreadable: true };
  });
}
